import React, { useState } from 'react'
import { useDrag } from 'react-dnd'
import Tooltip from './Tooltip';
import { useStore } from '../../store/store'
import { ItemTypes, BORDER_GRAY, getPrimaryDark } from '../../constants/constants'
import { insertableTemplatesByCategory } from '../../constants/containerTemplates'
import ContainerTemplatePreview from './ContainerTemplatePreview'
import GenerateDivider from './GenerateDivider'
import { FAMILY_ACCENTS, FAMILY_BACKGROUNDS } from '../../shared/ui/editorAccents'

/**
 * Galerie des dispositions de conteneur. Deux surfaces la montrent :
 *  - l'onglet Blocs du panneau de gauche, où l'on explore et où l'on glisse ;
 *  - la page vide, où l'on démarre.
 *
 * Volontairement absente du menu « + » entre deux conteneurs : celui-ci sert à
 * ajouter un élément, et y empiler un second catalogue en noyait le propos.
 * Poser une structure passe par l'onglet Blocs.
 *
 * Chaque tuile est à la fois cliquable et glissable : le clic pose au point
 * d'insertion connu de l'appelant, le glisser laisse viser un autre endroit.
 */

const CATEGORY_LABELS = {
  grid: 'Grilles',
  composition: 'Compositions',
}

// Famille de chaque catégorie : ses tuiles prennent la teinte pastel de la
// famille et son titre la couleur soutenue, comme les groupes de l'onglet
// Éléments (cf. BoxButtonsWrapper GROUP_BG_* / GROUP_ACCENT_*).
const CATEGORY_FAMILY = {
  grid: 'content',
  composition: 'layout',
}

const TemplateTile = ({ template, disabled, onPick, previewHeight, showLabel, category }) => {
  const [isHovered, setIsHovered] = useState(false)
  const isTemplateDocument = useStore(({ currentDocument }) => currentDocument?.type === 'template')
  const accentColor = getPrimaryDark(isTemplateDocument)

  const [, drag] = useDrag({
    type: ItemTypes.CONTAINER_TEMPLATE,
    // Charge minimale : un identifiant. Une copie de la disposition figée au
    // début du glisser vieillirait mal (catalogue versionné côté serveur plus
    // tard) et alourdirait chaque survol de cible.
    item: { templateId: template.id },
    canDrag: !disabled,
  })

  const hovered = isHovered && !disabled

  return (
    <Tooltip title={template.label} placement='top' mouseEnterDelay={0.4}>
      <div
        ref={drag}
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => !disabled && onPick?.(template)}
        onKeyDown={(e) => {
          if (disabled) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onPick?.(template)
          }
        }}
        style={{
          padding: 4,
          borderRadius: 5,
          cursor: disabled ? 'not-allowed' : (onPick ? 'pointer' : 'grab'),
          opacity: disabled ? 0.4 : 1,
          border: '1px solid',
          borderColor: hovered ? accentColor : BORDER_GRAY,
          backgroundColor: hovered ? '#EDEFF5' : (FAMILY_BACKGROUNDS[CATEGORY_FAMILY[category]] || 'transparent'),
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
        }}
      >
        <ContainerTemplatePreview columns={template.columns} height={previewHeight} />
        {showLabel && (
          <div
            lang='fr'
            style={{
              marginTop: 5,
              fontSize: 10,
              lineHeight: 1.2,
              fontWeight: 500,
              textAlign: 'center',
              color: 'rgba(11, 34, 57, 0.7)',
              // Même traitement que les vignettes d'élément (cf. BoxButton) :
              // une tuile de demi-panneau n'offre pas d'espace où couper un
              // libellé d'un seul mot long.
              hyphens: 'auto',
              overflowWrap: 'anywhere',
            }}
          >
            {template.label}
          </div>
        )}
      </div>
    </Tooltip>
  )
}

const ContainerTemplateGallery = ({
  onPick,
  columnsCount = 2,
  previewHeight = 30,
  showTemplateLabels = false,
}) => {
  const landscape = useStore(({ landscape }) => landscape)
  const maskEditing = useStore(({ maskEditing }) => maskEditing)
  const unlockProposal = useStore(({ configuration }) => configuration.unlockProposal)
  const isTemplateDocument = useStore(({ currentDocument }) => currentDocument?.type === 'template')

  // Mêmes verrous que les vignettes d'élément (cf. BoxButton) : proposition
  // verrouillée, édition de masque. En diapositive (paysage), le contenu est
  // positionné librement et la notion de grille de colonnes ne s'applique pas.
  const disabled = landscape || maskEditing.length > 0 || (!isTemplateDocument && !unlockProposal)

  const categories = ['grid', 'composition']

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {categories.map((category) => {
        const templates = insertableTemplatesByCategory(category)
        if (!templates.length) return null
        return (
          <div key={category}>
            <GenerateDivider
              text={CATEGORY_LABELS[category]}
              fullRow
              subtle
              accent={FAMILY_ACCENTS[CATEGORY_FAMILY[category]]}
              style={{ marginBottom: 5 }}
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columnsCount}, minmax(0, 1fr))`,
                gap: 6,
              }}
            >
              {templates.map((template) => (
                <TemplateTile
                  key={template.id}
                  template={template}
                  disabled={disabled}
                  onPick={onPick}
                  previewHeight={previewHeight}
                  showLabel={showTemplateLabels}
                  category={category}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ContainerTemplateGallery
